const liveSockets = new Map<WebSocket, string>();
const livePeerConnections = new Map<RTCPeerConnection, string>();

const WebSocketStates = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
} as const;

function isWebSocketLive(ws: WebSocket): boolean {
  return (
    ws.readyState === WebSocketStates.CONNECTING ||
    ws.readyState === WebSocketStates.OPEN
  );
}

function isPeerConnectionLive(pc: RTCPeerConnection): boolean {
  return pc.connectionState !== 'closed';
}

export function registerWebSocket(ws: WebSocket, ownerId: string): void {
  liveSockets.set(ws, ownerId);
}

export function registerPeerConnection(
  pc: RTCPeerConnection,
  ownerId: string
): void {
  livePeerConnections.set(pc, ownerId);
}

export function unregisterWebSocket(ws: WebSocket): void {
  liveSockets.delete(ws);
}

export function unregisterPeerConnection(pc: RTCPeerConnection): void {
  livePeerConnections.delete(pc);
}

export function getLiveResources(): {
  sockets: WebSocket[];
  peerConnections: RTCPeerConnection[];
} {
  return {
    sockets: Array.from(liveSockets.keys()),
    peerConnections: Array.from(livePeerConnections.keys()),
  };
}

export function clearRegistry(): void {
  liveSockets.clear();
  livePeerConnections.clear();
}

export interface SweepResult {
  clean: boolean;
  survivors: number;
}

function matchesOwner(entryOwnerId: string, ownerId?: string): boolean {
  return ownerId === undefined || entryOwnerId === ownerId;
}

export function verifyNoLiveResourcesAfterStop(ownerId?: string): SweepResult {
  let survivors = 0;

  for (const [ws, entryOwnerId] of liveSockets) {
    if (!matchesOwner(entryOwnerId, ownerId)) {
      continue;
    }

    if (isWebSocketLive(ws)) {
      survivors++;
      if (import.meta.env.MODE !== 'production') {
        console.error(
          '[ForkerResourceRegistry] Leftover WebSocket detected after stop',
          { readyState: ws.readyState, url: ws.url, ownerId: entryOwnerId }
        );
      }
      ws.close();
    }

    liveSockets.delete(ws);
  }

  for (const [pc, entryOwnerId] of livePeerConnections) {
    if (!matchesOwner(entryOwnerId, ownerId)) {
      continue;
    }

    if (isPeerConnectionLive(pc)) {
      survivors++;
      if (import.meta.env.MODE !== 'production') {
        console.error(
          '[ForkerResourceRegistry] Leftover RTCPeerConnection detected after stop',
          { connectionState: pc.connectionState, ownerId: entryOwnerId }
        );
      }
      pc.close();
    }

    livePeerConnections.delete(pc);
  }

  return {
    clean: survivors === 0,
    survivors,
  };
}
